// @vendor
import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';

// @components
import PromotionFullWidthCard from './PromotionFullWidthCard';

// @services
import PromotionService from '../../services/promotion.service';

// @interfaces
import { IPromotion, ISendPromotionRequest, ISendPromotionRequestPayload } from '../../domain/interfaces/Promotions';
import ModalRequestPromotion from './ModalRequestPromotion';

const PromotionFullWidthSlider: React.FC = () => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [promotions, setPromotions] = useState<IPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [serviceLocation, setServiceLocation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [promoId, setPromoId] = useState<string | null>(null);

  // Función para cargar promociones activas
  const loadActivePromotions = async () => {
    try {
      setLoading(true);
      setError(null);
      const activePromotions = await PromotionService.getActivePromotions();
      setPromotions(activePromotions);
    } catch (err) {
      setError('Error al cargar las promociones');
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar promociones al montar el componente
  useEffect(() => {
    loadActivePromotions();
  }, []);

  // Función para desplazar el slider
  const scroll = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const { scrollLeft, clientWidth } = sliderRef.current;
      const scrollTo = direction === 'left'
        ? scrollLeft - clientWidth
        : scrollLeft + clientWidth;

      sliderRef.current.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  };

  // Función para ir a un slide específico
  const goToSlide = (index: number) => {
    if (sliderRef.current) {
      const { clientWidth } = sliderRef.current;
      sliderRef.current.scrollTo({
        left: clientWidth * index,
        behavior: 'smooth'
      });
    }
  };

  // Actualizar la visibilidad de las flechas y el slide actual según la posición del scroll
  const handleScroll = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10); // 10px de margen para evitar problemas de redondeo

      // Actualizar el slide actual
      const newSlide = Math.round(scrollLeft / clientWidth);
      setCurrentSlide(newSlide);
    }
  };

  // Configurar el evento de scroll
  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Funciones para manejar eventos táctiles
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX - touchEndX > 100) {
      // Deslizar hacia la izquierda (siguiente slide)
      scroll('right');
    } else if (touchEndX - touchStartX > 100) {
      // Deslizar hacia la derecha (slide anterior)
      scroll('left');
    }
  };

  // Si está cargando, mostrar skeleton
  if (loading) {
    return (
      <div className="relative w-full py-10">
        {/* Título de la sección */}
        <div className="mb-8">
          <h2 className="text-center text-3xl md:text-4xl font-bold text-xervix-text-primary dark:text-xervix-text-primary-dark">
            Promociones <span className="text-xervix-primary">Especiales</span>
          </h2>
          <p className="text-left text-xervix-text-muted dark:text-xervix-text-muted-dark mt-2">
            Aprovecha estos descuentos exclusivos en servicios seleccionados
          </p>
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl h-96 w-full"></div>
            <div className="flex justify-center mt-6 space-x-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-3 w-3 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si hay error y no hay promociones, mostrar mensaje
  if (error && promotions.length === 0) {
    return (
      <div className="relative w-full py-10">
        {/* Título de la sección */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-xervix-text-primary dark:text-xervix-text-primary-dark">
            Promociones <span className="text-xervix-primary">Especiales</span>
          </h2>
          <p className="text-xervix-text-muted dark:text-xervix-text-muted-dark mt-2">
            Aprovecha estos descuentos exclusivos en servicios seleccionados
          </p>
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl p-8 text-center">
            <div className="text-gray-400 text-6xl mb-4">📢</div>
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
              No hay promociones disponibles
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              En este momento no hay promociones activas
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Si no hay promociones, no mostrar nada
  if (promotions.length === 0) {
    return null;
  }

  const handleSubmit = async (payload: ISendPromotionRequestPayload) => {
    try {
      setIsLoading(true);
      const serviceRequest = objectToSend(payload);
      await PromotionService.requestPromotion(serviceRequest);
      handleCloseModalRequestPromotion();
      setIsLoading(false);
      toast.success('Solicitud de descuento enviada correctamente, la empresa se pondrá en contacto contigo lo antes posible');
      //openWhatsApp(promotions.find(promotion => promotion.id === promoId) as IPromotion);
    } catch (error: any) {
      const errorMessage = error?.response?.data;
      setIsLoading(false);
      if (errorMessage && typeof errorMessage === 'string') return toast.error(errorMessage);
      toast.error('En este momento no se puede solicitar el descuento de la promoción, por favor intenta nuevamente mas tarde');
    }
  };

  const handleCloseModalRequestPromotion = () => {
    setIsOpen(false);
    setPhone('');
    setServiceLocation('');
    setPromoId(null);
  };

  const handleOpenModalRequestPromotion = async (promoId: string) => {
    const authToken = localStorage.getItem('auth_token');
    if (!authToken) return toast.error('Debes iniciar sesión para solicitar un descuento');
    setIsOpen(true);
    setPromoId(promoId);
  };

  /* const openWhatsApp = (promoData: IPromotion) => {
    const phoneNumber = `+57${promoData.companyPhone}`; // Número de WhatsApp
    const message = `Hola, estoy interesado en el descuento de la promoción ${promoData.title} con ${promoData.discount} de descuento ofrecido por ${promoData.companyName}.`;
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/[+\s]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  }; */

  const objectToSend = (payload: ISendPromotionRequestPayload) => {
    const userId = localStorage.getItem('user_id') || localStorage.getItem('company_id');
    const data: ISendPromotionRequest = {
      promotionId: promoId || "",
      cellPhone: payload.phone,
      address: payload.serviceLocation,
      userId: userId || ""
    };
    return data;
  };

  return (
    <div className="relative w-full md:py-10 py-4">
      {/* Título de la sección */}
      <div className=" mb-8">
        <h2 className="text-center text-3xl md:text-4xl font-bold text-xervix-text-primary dark:text-xervix-text-primary-dark">
          Promociones <span className="text-xervix-primary">Especiales</span>
        </h2>
        <p className="text-left text-xervix-text-muted dark:text-xervix-text-muted-dark mt-2">
          Aprovecha estos descuentos exclusivos en servicios seleccionados
        </p>
      </div>

      {/* Flecha izquierda */}
      {showLeftArrow && promotions.length > 1 && (
        <button
          onClick={() => scroll('left')}
          className="absolute md:left-[-40px] left-[-10px] top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800 rounded-full p-1 md:p-3 shadow-lg"
          aria-label="Anterior"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-xervix-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {showRightArrow && promotions.length > 1 && (
        <button
          onClick={() => scroll('right')}
          className="absolute md:right-[-40px] right-[-10px] top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800 rounded-full p-1 md:p-3 shadow-lg"
          aria-label="Siguiente"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-xervix-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Contenedor del slider */}
      <div
        ref={sliderRef}
        className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {promotions?.map((promotion) => (
          <div key={promotion.id} className="w-full flex-shrink-0 snap-start px-0 md:px-4">
            <PromotionFullWidthCard
              title={promotion.title}
              subtitle={promotion.description}
              discount={`${promotion.discount}%`}
              description={promotion.description}
              imageUrl={promotion.imageUrl}
              yearsExperience={promotion.yearsOfExperience}// Este campo no viene del backend, usar valor por defecto
              numberOfEmployees={promotion.numberOfEmployees} // Este campo no viene del backend, usar valor por defecto
              companyName={promotion.companyName}
              endDate={promotion.endDate}
              onClick={() => handleOpenModalRequestPromotion(promotion?.id)}
            />
          </div>
        ))}
      </div>

      {/* Indicadores de slide - solo mostrar si hay más de una promoción */}
      {promotions.length > 1 && (
        <div className="flex justify-center mt-6 space-x-2">
          {promotions.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-3 w-3 rounded-full transition-all ${currentSlide === index
                ? 'bg-xervix-primary w-6'
                : 'bg-gray-300 dark:bg-gray-600'
                }`}
              aria-label={`Ir al slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      <ModalRequestPromotion
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={handleSubmit}
        phone={phone}
        serviceLocation={serviceLocation}
        setPhone={setPhone}
        setServiceLocation={setServiceLocation}
        isLoading={isLoading}
      />
    </div>
  );
};

export default PromotionFullWidthSlider;
